import {
  Button,
  EditIcon,
  EmbedCodeIcon,
  Flex,
  SwitchField,
  Typography,
  useToast,
} from '@iheartjane/reefer';
import {
  Form,
  FormValidationError,
  useForm,
} from '@iheartjane/reefer-hook-form';
import { pluralize } from '@iheartjane/utils';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { ConfirmDisableModal } from '@jane/business-admin/components/ConfirmDisableModal';
import { useUpdateMenuRow } from '@jane/business-admin/data-access';
import {
  useCatchErrorsWithManager,
  useStoreId,
} from '@jane/business-admin/hooks';
import type { MenuRow } from '@jane/business-admin/types';
import {
  EventNames,
  parseValidationErrors,
  track,
} from '@jane/business-admin/util';

interface HeaderEditButtonProps {
  onOpenMenuRow: () => void;
  showCTAButton: boolean;
}

export const HeaderEditButton = ({
  onOpenMenuRow,
  showCTAButton,
}: HeaderEditButtonProps) => {
  return showCTAButton ? (
    <Button label="Add products" onClick={onOpenMenuRow} ml={16} />
  ) : (
    <Button.Icon
      icon={<EditIcon />}
      variant="secondary"
      label="Edit Menu Row"
      onClick={onOpenMenuRow}
      data-testid="menu-row-edit-button"
      ml={16}
    />
  );
};

export const EmbedCodeButton = ({
  hasEmbedCode = false,
  onClick,
}: {
  hasEmbedCode: boolean;
  onClick: () => void;
}) => {
  return (
    <Button
      label={`${hasEmbedCode ? 'View' : 'Create'} embed code`}
      variant="tertiary"
      startIcon={<EmbedCodeIcon />}
      onClick={onClick}
    />
  );
};

interface HeaderProps {
  hasEmbeddedMenuRow?: boolean;
  itemsLength: number;
  row: MenuRow;
  rowHelperText?: ReactNode;
  rowLabel: string;
  showProductsCTA: boolean;
}

export const HeaderWithoutToggle = ({
  row,
  rowLabel,
  itemsLength,
}: Omit<HeaderProps, 'showProductsCTA'>) => {
  const rowProductText =
    row.is_category && itemsLength > 0
      ? `${itemsLength} items`
      : 'No inventory';

  return (
    <Flex width="100%" justifyContent="space-between" alignItems="center">
      <Typography as="h2" branded variant="h2-semibold">
        {rowLabel}
      </Typography>
      <Typography>{rowProductText}</Typography>
    </Flex>
  );
};

export const HeaderToggle = ({
  hasEmbeddedMenuRow,
  row,
  rowLabel,
  itemsLength,
  showProductsCTA,
  rowHelperText,
}: HeaderProps) => {
  const toast = useToast();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const FIELD_NAME = `enabled_${row.id}`;
  const formMethods = useForm({
    defaultValues: {
      [FIELD_NAME]: row.enabled,
    },
  });
  const { setValue, watch } = formMethods;
  const enabled = watch(FIELD_NAME);

  const switchFieldProps = {
    name: FIELD_NAME,
    helperText: rowHelperText,
    label: (
      <Typography as="h2" branded variant="h2-semibold">
        {rowLabel}
      </Typography>
    ),
  };

  const rowTypeValue = row.menu_row_type;

  const brandSpecialsRow = rowTypeValue === 'brand_specials';
  const displayProductCount = ![
    'specials',
    'magic_row',
    'buy_again_row',
    'brand_specials',
  ].includes(rowTypeValue ?? '');

  const catchSubmitErrors = useCatchErrorsWithManager(
    `Error editing menu row. Please try again.`
  );

  const storeId = useStoreId();
  const { mutateAsync: updateMenuRow, isSuccess: updateSuccess } =
    useUpdateMenuRow({ rowId: row.id || 0, storeId });

  const onToggle = useCallback(
    (toggled: boolean) => {
      if (!row || toggled === row.enabled) {
        return;
      }

      const { enabled: _enabled, ...rowRest } = row;
      const requestData = {
        ...rowRest,
        custom_display_name: row.custom_display_name || rowTypeValue,
        schedule: { ...row.schedule, enabled: toggled },
      };
      const submitMethod = async () => {
        await updateMenuRow(requestData as any);

        track({
          event: EventNames.ToggleVisibility,
          object: 'menu row',
          final_state: toggled ? 'visible' : 'hidden',
          trigger_source_id: 'menu index page',
          successful: true,
        });
      };

      return catchSubmitErrors({
        submitMethod,
        requestData,
        onValidationError: (validationErrors: Record<string, unknown>) => {
          track({
            event: EventNames.ToggleVisibility,
            object: 'menu row',
            final_state: toggled ? 'visible' : 'hidden',
            trigger_source_id: 'menu index page',
            successful: false,
            error_reason: '400 Bad Request',
          });
          throw new FormValidationError(
            'menu-row-error',
            parseValidationErrors(validationErrors)
          );
        },
        callback: () => {
          setValue(FIELD_NAME, row.enabled);
          track({
            event: EventNames.ToggleVisibility,
            object: 'menu row',
            final_state: toggled ? 'visible' : 'hidden',
            trigger_source_id: 'menu index page',
            successful: false,
            error_reason: 'Server error',
          });
          toast.add({
            label: 'Error updating menu row settings. Please try again.',
            variant: 'error',
          });
        },
      });
    },
    [row, rowTypeValue, updateMenuRow, catchSubmitErrors, toast, setValue]
  );

  useEffect(() => {
    if (updateSuccess) {
      toast.add({
        label: 'Menu row settings updated',
        variant: 'success',
      });
    }
  }, [updateSuccess]);

  useEffect(() => {
    setValue(FIELD_NAME, row.enabled);
  }, [row.enabled, setValue]);

  return (
    <Flex grow={1} justifyContent="space-between" alignItems="center">
      {/* // TODO: Label is misaligned with the toggle, requires adding a flex property to the FieldWrapper thats on SwitchField */}
      {brandSpecialsRow || hasEmbeddedMenuRow ? (
        <>
          <SwitchField
            {...switchFieldProps}
            onClick={(evt) => {
              evt.preventDefault();
              if (enabled) {
                setOpenConfirmModal(true);
              } else {
                onToggle(true);
              }
            }}
            checked={enabled}
          />
          <ConfirmDisableModal
            itemName={brandSpecialsRow ? 'row' : rowLabel}
            type={brandSpecialsRow ? 'gold' : 'embedded'}
            onConfirm={() => {
              setOpenConfirmModal(false);
              onToggle(false);
            }}
            onCancel={() => {
              setOpenConfirmModal(false);
            }}
            open={openConfirmModal}
          />
        </>
      ) : (
        <Form.BaseForm
          name="menu-row-toggle"
          formMethods={formMethods}
          onSubmit={() => null}
        >
          <Form.SwitchField {...switchFieldProps} onChange={onToggle} />
        </Form.BaseForm>
      )}
      {displayProductCount && (
        <Typography>
          {showProductsCTA
            ? 'No products added'
            : `${itemsLength} in-stock ${pluralize({ noun: 'product', number: itemsLength, returnValue: 'word' })}`}
        </Typography>
      )}
    </Flex>
  );
};
